/** Modal width: A4 page + small margin on large screens; nearly full viewport on small. */
export const BILL_PREVIEW_MODAL_MAX_WIDTH = 'max-w-[95vw] lg:max-w-[220mm]';

/** Inner column wrapping the bill page shell. */
export const BILL_PREVIEW_INNER_CLASS = 'w-full max-w-full lg:max-w-[210mm] mx-auto';

/** Shell class for the bill page wrapper inside PreviewModal. */
export const BILL_PREVIEW_SHELL_CLASS =
  'invoice-preview-page-shell w-full max-w-full lg:w-[210mm] lg:max-w-[210mm] mx-auto print:shadow-none print:bg-white print:transform-none print:p-0 print:m-0 print:max-w-none print:w-[210mm] print:min-h-[297mm]';

/** Inline styles for preview shell (no fixed width — CSS classes handle responsive sizing). */
export const BILL_PREVIEW_SHELL_STYLE = {
  background: 'white',
  boxShadow: '0 0 8px #ccc',
  margin: '0 auto',
  padding: 0,
  height: 'auto',
  minHeight: 'auto',
};

/** Initial zoom when opening preview: 1 on all viewports (CSS handles mobile fit). */
export function getBillPreviewInitialZoom() {
  return 1;
}

/** Fit A4 bill width (~210mm) into the available preview column on narrow screens (legacy). */
export function computeBillPreviewFitZoom(viewportWidthPx) {
  const padding = 24;
  const a4WidthPx = (210 / 25.4) * 96;
  const available = Math.max(280, (viewportWidthPx || window.innerWidth) - padding);
  return Math.min(1, Math.max(0.35, available / a4WidthPx));
}

/**
 * Screen-only bill preview: full 210mm on wide viewports; fluid width on phones.
 */
export const BILL_PREVIEW_SCREEN_CSS = `
@media screen {
  [data-acctoo-bill-preview="true"].invoice-preview-page-shell {
    box-sizing: border-box !important;
    margin-left: auto !important;
    margin-right: auto !important;
    transform: none !important;
  }

  @media screen and (min-width: 900px) {
    [data-acctoo-bill-preview="true"].invoice-preview-page-shell {
      width: 210mm !important;
      max-width: 210mm !important;
    }
    [data-acctoo-bill-preview="true"] #invoice-template,
    [data-acctoo-bill-preview="true"] #quotation-template,
    [data-acctoo-bill-preview="true"] #challan-template,
    [data-acctoo-bill-preview="true"] #modern-sales-template,
    [data-acctoo-bill-preview="true"] #simple-sales-template,
    [data-acctoo-bill-preview="true"] #invoice-a5-template {
      width: 210mm !important;
      max-width: 210mm !important;
      min-height: auto !important;
      box-sizing: border-box !important;
    }
    [data-acctoo-bill-preview="true"] #invoice-template.invoice-screen-preview-boost,
    [data-acctoo-bill-preview="true"] #quotation-template.quotation-screen-preview-boost {
      font-size: 100% !important;
    }
    [data-acctoo-bill-preview="true"] #invoice-template .invoice-print-inner {
      transform: none !important;
      width: 100% !important;
      max-width: 100% !important;
    }
    [data-acctoo-bill-preview="true"].invoice-preview-page-shell > * {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
  }

  @media screen and (max-width: 899px) {
    [data-acctoo-bill-preview="true"].invoice-preview-page-shell {
      width: 100% !important;
      max-width: 100% !important;
      box-shadow: none !important;
    }
    [data-acctoo-bill-preview="true"] #invoice-template,
    [data-acctoo-bill-preview="true"] #quotation-template,
    [data-acctoo-bill-preview="true"] #challan-template,
    [data-acctoo-bill-preview="true"] #modern-sales-template,
    [data-acctoo-bill-preview="true"] #simple-sales-template,
    [data-acctoo-bill-preview="true"] #invoice-a5-template {
      width: 100% !important;
      max-width: 100% !important;
      min-height: auto !important;
      box-sizing: border-box !important;
      font-size: 11px !important;
      overflow-x: hidden !important;
    }
    [data-acctoo-bill-preview="true"] #invoice-template,
    [data-acctoo-bill-preview="true"] #quotation-template {
      padding: 8px !important;
      border-radius: 4px !important;
    }
    [data-acctoo-bill-preview="true"] table,
    [data-acctoo-bill-preview="true"] .quotation-items-table {
      min-width: 0 !important;
      width: 100% !important;
      font-size: 10px !important;
      table-layout: fixed !important;
    }
    [data-acctoo-bill-preview="true"] th,
    [data-acctoo-bill-preview="true"] td {
      word-break: break-word !important;
      white-space: normal !important;
      overflow-wrap: anywhere !important;
      padding: 4px 6px !important;
    }
    [data-acctoo-bill-preview="true"] .quotation-table-wrap,
    [data-acctoo-bill-preview="true"] #invoice-template .invoice-table-wrap,
    [data-acctoo-bill-preview="true"] .invoice-table-wrap {
      max-width: 100% !important;
      overflow-x: visible !important;
    }
    [data-acctoo-bill-preview="true"] .quotation-doc-header > div:first-child {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 0.75rem !important;
      padding: 12px !important;
    }
    [data-acctoo-bill-preview="true"] .quotation-doc-header .text-right {
      text-align: left !important;
      width: 100% !important;
      margin-top: 0.5rem !important;
    }
    [data-acctoo-bill-preview="true"] .quotation-doc-header .text-3xl {
      font-size: 1.25rem !important;
    }
    [data-acctoo-bill-preview="true"] .quotation-doc-header .text-2xl {
      font-size: 1.125rem !important;
    }
    [data-acctoo-bill-preview="true"] .quotation-post-items-row {
      flex-direction: column !important;
    }
    [data-acctoo-bill-preview="true"] .quotation-bank-box,
    [data-acctoo-bill-preview="true"] .quotation-terms-box {
      width: 100% !important;
    }
    [data-acctoo-bill-preview="true"] .quotation-signature-row {
      flex-wrap: wrap !important;
      justify-content: center !important;
      gap: 1rem !important;
    }
  }

  @media screen and (max-width: 767px) {
    [data-acctoo-bill-preview="true"] #invoice-template .invoice-table-wrap,
    [data-acctoo-bill-preview="true"] .quotation-table-wrap {
      max-width: 100% !important;
      overflow-x: visible !important;
    }
  }

  @media screen and (min-width: 768px) and (max-width: 899px) {
    [data-acctoo-bill-preview="true"] #invoice-template .invoice-table-wrap,
    [data-acctoo-bill-preview="true"] .quotation-table-wrap {
      max-width: 100% !important;
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch !important;
    }
  }

  @media screen and (min-width: 900px) {
    [data-acctoo-bill-preview="true"] #invoice-template .invoice-table-wrap,
    [data-acctoo-bill-preview="true"] .quotation-table-wrap {
      max-width: 100% !important;
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch !important;
    }
  }
}
`;
